/**
 * MonthCalendar.jsx
 * Path: frontend/src/components/MonthCalendar.jsx
 * Description: Widok miesięczny z rezerwacjami renderowanymi wewnątrz kart dni oraz oznaczeniami niedziel i świąt.
 * Related: pages/CalendarPage.jsx, utils/polishCalendar.js
 */

import { useEffect, useMemo, useState } from "react";
import { isSameDayRange, toIsoDate } from "../utils/date";
import { getPolishPublicHolidays } from "../utils/polishCalendar";

const WEEKDAY_LABELS = [
  { label: "Pon", isSunday: false },
  { label: "Wt", isSunday: false },
  { label: "Śr", isSunday: false },
  { label: "Czw", isSunday: false },
  { label: "Pt", isSunday: false },
  { label: "Sob", isSunday: false },
  { label: "Nd", isSunday: true },
];

const CALENDAR_COLOR_PALETTE = [
  "linear-gradient(135deg, #f0542c 0%, #d44823 100%)",
  "linear-gradient(135deg, #227ca7 0%, #0f5f8e 100%)",
  "linear-gradient(135deg, #1a8a57 0%, #0f6a3c 100%)",
  "linear-gradient(135deg, #9156b4 0%, #6c3f8f 100%)",
  "linear-gradient(135deg, #c26d1b 0%, #8d4f0f 100%)",
  "linear-gradient(135deg, #a33e68 0%, #7a2b4e 100%)",
];

const CLOSE_BOOKING_GAP_DAYS = 30;

function buildCells(monthDate, holidayMap) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const leading = (firstDay.getDay() + 6) % 7;
  const todayIso = toIsoDate(new Date());
  const cells = [];

  for (let index = 0; index < leading; index += 1) {
    cells.push({ key: `blank-start-${index}`, blank: true });
  }

  for (let day = 1; day <= lastDay.getDate(); day += 1) {
    const date = new Date(year, month, day);
    const dateIso = toIsoDate(date);
    const holidayNames = holidayMap.get(dateIso) || [];
    const weekdayIndex = date.getDay();

    cells.push({
      key: dateIso,
      blank: false,
      dateIso,
      day,
      isToday: dateIso === todayIso,
      isSunday: weekdayIndex === 0,
      isHoliday: holidayNames.length > 0,
      holidayNames,
    });
  }

  while (cells.length % 7 !== 0) {
    cells.push({ key: `blank-end-${cells.length}`, blank: true });
  }

  return cells;
}

function splitIntoWeeks(cells) {
  const weeks = [];

  for (let index = 0; index < cells.length; index += 7) {
    weeks.push(cells.slice(index, index + 7));
  }

  return weeks;
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleTimeString("pl-PL", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function buildJobSubtitle(job, compact) {
  if (compact) {
    return "";
  }

  if (job.customerName || job.customer_name) {
    return job.customerName || job.customer_name;
  }

  const start = formatTime(job.startAt || job.start_at);
  const end = formatTime(job.endAt || job.end_at);

  if (start && end) {
    return `${start}-${end}`;
  }

  return "";
}

function parseHexColor(value) {
  const match = String(value || "").trim().match(/^#([0-9a-fA-F]{6})$/);
  if (!match) {
    return null;
  }
  return {
    r: Number.parseInt(match[1].slice(0, 2), 16),
    g: Number.parseInt(match[1].slice(2, 4), 16),
    b: Number.parseInt(match[1].slice(4, 6), 16),
  };
}

function toRelativeLuminance({ r, g, b }) {
  const transform = (channel) => {
    const normalized = channel / 255;
    if (normalized <= 0.03928) {
      return normalized / 12.92;
    }
    return ((normalized + 0.055) / 1.055) ** 2.4;
  };
  const red = transform(r);
  const green = transform(g);
  const blue = transform(b);
  return red * 0.2126 + green * 0.7152 + blue * 0.0722;
}

function resolveBarStyle(job, fallbackGradient) {
  const parsed = parseHexColor(job.color);
  if (!parsed) {
    return { background: fallbackGradient, color: "#ffffff" };
  }
  const luminance = toRelativeLuminance(parsed);
  return {
    background: job.color,
    color: luminance > 0.45 ? "#1f2933" : "#ffffff",
  };
}

function normalizeDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
}

function areCloseInTime(left, right, gapDays) {
  if (!left.start || !left.end || !right.start || !right.end) {
    return false;
  }
  const maxGapMs = gapDays * 24 * 60 * 60 * 1000;
  if (left.start <= right.end && right.start <= left.end) {
    return true;
  }
  if (left.end < right.start) {
    return right.start.getTime() - left.end.getTime() <= maxGapMs;
  }
  return left.start.getTime() - right.end.getTime() <= maxGapMs;
}

function buildJobColorMap(jobs) {
  const nodes = jobs
    .map((job) => ({
      id: String(job.id || ""),
      start: normalizeDate(job.startAt || job.start_at),
      end: normalizeDate(job.endAt || job.end_at),
      fallbackOrder: String(job.id || job.title || ""),
    }))
    .filter((item) => item.id && item.start && item.end)
    .sort((left, right) => {
      if (left.start.getTime() !== right.start.getTime()) {
        return left.start.getTime() - right.start.getTime();
      }
      if (left.end.getTime() !== right.end.getTime()) {
        return left.end.getTime() - right.end.getTime();
      }
      return left.fallbackOrder.localeCompare(right.fallbackOrder, "pl");
    });

  const colorByJobId = new Map();

  nodes.forEach((node, index) => {
    const unavailable = new Set();
    for (let neighborIndex = 0; neighborIndex < index; neighborIndex += 1) {
      const neighbor = nodes[neighborIndex];
      if (!areCloseInTime(node, neighbor, CLOSE_BOOKING_GAP_DAYS)) {
        continue;
      }
      const usedColor = colorByJobId.get(neighbor.id);
      if (typeof usedColor === "number") {
        unavailable.add(usedColor);
      }
    }

    let selected = 0;
    for (let paletteIndex = 0; paletteIndex < CALENDAR_COLOR_PALETTE.length; paletteIndex += 1) {
      if (!unavailable.has(paletteIndex)) {
        selected = paletteIndex;
        break;
      }
    }
    colorByJobId.set(node.id, selected);
  });

  return colorByJobId;
}

function buildWeekLayout(weekCells, jobs, maxLanes) {
  const rawSegments = jobs
    .map((job) => {
      const visibleIndexes = weekCells
        .map((cell, index) => (cell.blank ? null : isSameDayRange(job, cell.dateIso) ? index : null))
        .filter((value) => value !== null);

      if (visibleIndexes.length === 0) {
        return null;
      }

      const startIndex = visibleIndexes[0];
      const endIndex = visibleIndexes[visibleIndexes.length - 1];

      return {
        job,
        startIndex,
        endIndex,
        span: endIndex - startIndex + 1,
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      if (left.startIndex !== right.startIndex) {
        return left.startIndex - right.startIndex;
      }

      if (left.span !== right.span) {
        return right.span - left.span;
      }

      return String(left.job.title || "").localeCompare(String(right.job.title || ""), "pl");
    });

  const laneEnds = [];
  const visibleSegments = [];
  const hiddenCounts = Array.from({ length: 7 }, () => 0);
  const dayTotals = Array.from({ length: 7 }, () => 0);

  rawSegments.forEach((segment) => {
    for (let index = segment.startIndex; index <= segment.endIndex; index += 1) {
      dayTotals[index] += 1;
    }

    let laneIndex = -1;

    for (let index = 0; index < maxLanes; index += 1) {
      if (laneEnds[index] === undefined || laneEnds[index] < segment.startIndex) {
        laneIndex = index;
        laneEnds[index] = segment.endIndex;
        break;
      }
    }

    if (laneIndex === -1) {
      for (let index = segment.startIndex; index <= segment.endIndex; index += 1) {
        hiddenCounts[index] += 1;
      }
      return;
    }

    visibleSegments.push({
      ...segment,
      key: `${segment.job.id}-${laneIndex}-${segment.startIndex}-${segment.endIndex}`,
      lane: laneIndex,
    });
  });

  return {
    visibleSegments,
    hiddenCounts,
    dayTotals,
    laneCount: visibleSegments.length ? Math.max(...visibleSegments.map((item) => item.lane)) + 1 : 0,
  };
}

function buildDayTag(cell, dayJobsCount) {
  if (cell.isHoliday) return "Święto";
  if (cell.isSunday) return "Nd";
  if (dayJobsCount > 0) return `${dayJobsCount} zle.`;
  return "";
}

function buildDayTitle(cell) {
  if (cell.isHoliday) {
    return cell.holidayNames.join(", ");
  }

  if (cell.isSunday) {
    return "Niedziela";
  }

  return "";
}

export default function MonthCalendar({ monthDate, jobs, onDayClick, onJobClick }) {
  const [compact, setCompact] = useState(() => window.innerWidth <= 980);
  const holidayMap = useMemo(() => getPolishPublicHolidays(monthDate.getFullYear()), [monthDate]);
  const cells = useMemo(() => buildCells(monthDate, holidayMap), [monthDate, holidayMap]);
  const weeks = useMemo(() => splitIntoWeeks(cells), [cells]);
  const jobColorMap = useMemo(() => buildJobColorMap(jobs), [jobs]);
  const rowCount = weeks.length;
  const maxLanes = compact ? (rowCount >= 6 ? 2 : 3) : rowCount >= 6 ? 3 : 4;

  useEffect(() => {
    const media = window.matchMedia("(max-width: 980px)");
    const updateCompact = () => setCompact(media.matches);

    updateCompact();
    media.addEventListener("change", updateCompact);

    return () => media.removeEventListener("change", updateCompact);
  }, []);

  return (
    <section className="calendar-grid" style={{ "--calendar-rows": rowCount }}>
      <div className="calendar-weekdays">
        {WEEKDAY_LABELS.map((weekday) => (
          <div
            key={weekday.label}
            className={`calendar-weekday ${weekday.isSunday ? "calendar-weekday-sunday" : ""}`}
          >
            {weekday.label}
          </div>
        ))}
      </div>

      <div className="calendar-weeks">
        {weeks.map((weekCells, weekIndex) => {
          const { visibleSegments, hiddenCounts, dayTotals, laneCount } = buildWeekLayout(weekCells, jobs, maxLanes);

          return (
            <section
              key={`week-${weekIndex}`}
              className="calendar-week-row"
              style={{ "--calendar-lanes": Math.max(laneCount, 1) }}
            >
              {visibleSegments.length > 0 ? (
                <div className="calendar-week-bars">
                  {visibleSegments.map((segment) => {
                    const subtitle = buildJobSubtitle(segment.job, compact);
                    const fallbackGradient =
                      CALENDAR_COLOR_PALETTE[
                        jobColorMap.get(String(segment.job.id || "")) ?? 0
                      ];
                    const barStyle = resolveBarStyle(segment.job, fallbackGradient);

                    return (
                      <button
                        type="button"
                        key={segment.key}
                        className="calendar-span-bar calendar-week-span"
                        style={{
                          gridColumn: `${segment.startIndex + 1} / span ${segment.span}`,
                          gridRow: `${segment.lane + 1}`,
                          background: barStyle.background,
                          color: barStyle.color,
                        }}
                        title={segment.job.title || "Zlecenie"}
                        onClick={() => onJobClick(segment.job.id)}
                      >
                        <span className="calendar-span-title">{segment.job.title || "Zlecenie"}</span>
                        {subtitle ? <span className="calendar-span-subtitle">{subtitle}</span> : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}

              <div className="calendar-week-days">
                {weekCells.map((cell, dayIndex) => {
                  if (cell.blank) {
                    return <div key={cell.key} className="calendar-cell calendar-cell-blank" />;
                  }

                  const dayJobsCount = dayTotals[dayIndex];
                  const dayTitle = buildDayTitle(cell);
                  const dayTag = buildDayTag(cell, dayJobsCount);

                  return (
                    <div
                      key={cell.key}
                      className={[
                        "calendar-cell",
                        cell.isToday ? "calendar-cell-today" : "",
                        cell.isSunday ? "calendar-cell-sunday" : "",
                        cell.isHoliday ? "calendar-cell-holiday" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      role="button"
                      tabIndex={0}
                      title={dayTitle || undefined}
                      onClick={() => onDayClick(cell.dateIso)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onDayClick(cell.dateIso);
                        }
                      }}
                    >
                      <div className="calendar-cell-header">
                        <span className="calendar-cell-day">{cell.day}</span>
                        {dayTag ? (
                          <span className="calendar-cell-tag" title={dayTitle || dayTag}>
                            {dayTag}
                          </span>
                        ) : null}
                      </div>

                      <div className="calendar-cell-body">
                        <div className="calendar-cell-bar-slot" />

                        {hiddenCounts[dayIndex] > 0 ? (
                          <div className="calendar-more">+{hiddenCounts[dayIndex]} więcej</div>
                        ) : dayJobsCount === 0 ? (
                          <div className="calendar-cell-empty">{cell.isHoliday || cell.isSunday ? "Dzień wolny" : "Wolne"}</div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </section>
  );
}
